import React from "react";
import {
  Dialog,
  DialogContent,
  IconButton,
  Box,
  Typography,
  Divider,
  Alert,
  Button,
  Chip,
  Slide,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import BeachAccessIcon from "@mui/icons-material/BeachAccess";
import { useSelector, useDispatch } from "react-redux";

import PayPal from "./PayPal";
import ConfirmationDialog from "./ConfirmationDialog";
import { saveCart, resetConf } from "../features/confirmationSlice";
import { EVENT_TITLE, RATE_PER_DAY } from "../config";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const money = (value) => `$${Number(value || 0).toFixed(2)}`;

const utcFormat = (dateString, options) =>
  new Intl.DateTimeFormat("en-US", { timeZone: "UTC", ...options }).format(
    new Date(dateString),
  );

const DayRow = ({ date, count, discount, lineTotal }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 1 }}>
    <Box
      sx={{
        width: 56,
        flexShrink: 0,
        textAlign: "center",
        border: 1,
        borderColor: "grey.300",
        borderRadius: 2,
        py: 0.5,
        bgcolor: "background.default",
      }}
    >
      <Typography
        variant="overline"
        color="primary"
        sx={{ display: "block", lineHeight: 1.4 }}
      >
        {utcFormat(date, { weekday: "short" })}
      </Typography>
      <Typography variant="subtitle2" sx={{ lineHeight: 1.2 }}>
        {utcFormat(date, { month: "short", day: "numeric" })}
      </Typography>
    </Box>
    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
      <Typography variant="body2">
        {count} {count === 1 ? "set" : "sets"} × {money(RATE_PER_DAY)}
      </Typography>
      {discount > 0 && (
        <Typography variant="caption" color="success.main">
          − {money(discount)} multi-day rate
        </Typography>
      )}
    </Box>
    <Typography variant="subtitle2">{money(lineTotal)}</Typography>
  </Box>
);

const SummaryRow = ({ label, value, color }) => (
  <Box sx={{ display: "flex", justifyContent: "space-between", py: 0.5 }}>
    <Typography variant="body2" color={color || "text.secondary"}>
      {label}
    </Typography>
    <Typography variant="body2" color={color || "text.primary"}>
      {value}
    </Typography>
  </Box>
);

const CheckoutDialog = ({ open, onClose, total, cart }) => {
  const { user } = useSelector((state) => state.auth);
  const { confirmation } = useSelector((state) => state.conf);
  const [tip, setTip] = React.useState(0);
  const [updatedTotal, setUpdatedTotal] = React.useState(total);
  const dispatch = useDispatch();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  React.useEffect(() => {
    setTip(parseFloat((total * 0.2).toFixed(2)));
  }, [total]);

  const { dates, sets, totals, discounts, discountedTotals } = cart;
  const notes =
    user &&
    `Name: ${user.fullName} \r\n
    Unit: ${user.building}-${user.unit} Chair Sets: ${dates
      .map((d, i) => {
        if (!sets[i]) return null;
        const date = new Date(d);
        const f = `${date.getUTCMonth() + 1}/${date.getUTCDate()}`;
        return `[${f}: ${sets[i]}]`;
      })
      .filter(Boolean)} Tip: $ ${tip} UID: ${user.uid}`;

  React.useEffect(() => {
    if (!user) return;
    if (!open) return;
    dispatch(saveCart({ ...cart, uid: user.uid }));
    // Save once each time the dialog opens; cart is a new object every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  React.useEffect(() => {
    setUpdatedTotal(parseFloat(total) + parseFloat(tip));
  }, [total, tip]);

  const subtotal = totals.reduce((sum, t) => sum + t, 0);
  const savings = discounts.reduce((sum, d) => sum + d, 0);
  const bookedCount = sets.filter(Boolean).length;
  const paypalTotal = ((updatedTotal + 0.5) / 0.965).toFixed(2);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      fullWidth
      maxWidth="xs"
      TransitionComponent={Transition}
    >
      <Box
        sx={{
          position: "relative",
          px: 3,
          py: 2.5,
          color: "common.white",
          textAlign: "center",
          background:
            "linear-gradient(115deg, #004a58 0%, #00687b 55%, #00829a 100%)",
        }}
      >
        <IconButton
          onClick={onClose}
          sx={{ position: "absolute", top: 8, right: 8, color: "rgba(255,255,255,0.8)" }}
        >
          <CloseIcon />
        </IconButton>
        <Box
          sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 1 }}
        >
          <BeachAccessIcon fontSize="small" />
          <Typography variant="h6">Checkout</Typography>
        </Box>
        <Typography variant="body2" sx={{ opacity: 0.8 }}>
          {EVENT_TITLE} · Tops'l Beach
        </Typography>
      </Box>

      <DialogContent sx={{ px: 3, py: 2.5 }}>
        {bookedCount === 0 ? (
          <Typography color="text.secondary" sx={{ textAlign: "center", my: 3 }}>
            No days selected yet — close this window and choose your days.
          </Typography>
        ) : (
          <>
            <Typography variant="overline" color="text.secondary">
              Your sets
            </Typography>
            {dates.map((date, i) => {
              if (!sets[i]) return null;
              return (
                <DayRow
                  key={date}
                  date={date}
                  count={sets[i]}
                  discount={discounts[i]}
                  lineTotal={discountedTotals[i]}
                />
              );
            })}

            <Divider sx={{ my: 1.5, borderStyle: "dashed" }} />

            <SummaryRow label="Subtotal" value={money(subtotal)} />
            {savings > 0 && (
              <SummaryRow
                label="Multi-day savings"
                value={`− ${money(savings)}`}
                color="success.main"
              />
            )}
            <SummaryRow label="Beach attendant tip (20%)" value={money(tip)} />
            <Typography variant="caption" color="text.secondary">
              Beach service requires an included tip for groups.
            </Typography>

            <Box
              sx={{
                mt: 2,
                px: 2,
                py: 1.5,
                borderRadius: 2,
                bgcolor: "background.default",
                border: 1,
                borderColor: "grey.200",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography variant="h6">Total</Typography>
              <Typography variant="h5" sx={{ fontWeight: 800, color: "primary.dark" }}>
                {money(updatedTotal)}
              </Typography>
            </Box>
            {savings > 0 && (
              <Box sx={{ textAlign: "center", mt: 1.5 }}>
                <Chip
                  size="small"
                  color="success"
                  variant="outlined"
                  label={`You're saving ${money(savings)} with multi-day rates`}
                />
              </Box>
            )}
          </>
        )}

        <Box sx={{ mt: 3 }}>
          {confirmation ? (
            <Box
              sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}
            >
              <Alert severity="success" sx={{ width: "100%" }}>
                {`Confirmed! Ref #: ${confirmation?.referenceId}`}
              </Alert>
              <Button onClick={() => dispatch(resetConf())}>Reset Payment</Button>
            </Box>
          ) : (
            <>
              <Typography variant="overline" color="text.secondary">
                Payment
              </Typography>
              <ConfirmationDialog
                username="9858594936"
                amount={updatedTotal.toFixed(2)}
                notes={notes}
                cart={cart}
              />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", textAlign: "center", mt: 0.5 }}
              >
                Venmo · no fees
              </Typography>
              <Divider sx={{ my: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  or
                </Typography>
              </Divider>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", textAlign: "center", mb: 1 }}
              >
                Card / PayPal · {money(paypalTotal)} incl. processing fee
              </Typography>
              <PayPal total={paypalTotal} notes={notes} cart={cart} />
            </>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default CheckoutDialog;
